import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import './TimeEntry.css';

const getBasePath = () => '/databaseOps/';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Convert HH:MM string to milliseconds from midnight
const timeToMs = (hh: number, mm: number): number => (hh * 3600000) + (mm * 60000);

// Convert milliseconds from midnight to HH:MM string
const msToTime = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Convert milliseconds duration to human-readable
const msToDuration = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (hours === 0) return `${minutes} Min.`;
  if (minutes === 0) return `${hours} Std.`;
  return `${hours} Std. ${minutes} Min.`;
};

interface WorktimeEntry {
  ID: number;
  Mitarbeiter: number;
  Datum: number;
  Beginn: number;
  Ende: number;
  Pause: number;
  Bemerkung: string;
  Arbeitszeit: number;
}

const TimeEntry: React.FC = () => {
  const { user } = useAuth();

  // Form state
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('12:00');
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [bemerkung, setBemerkung] = useState('');

  // Data state
  const [remarks, setRemarks] = useState<string[]>([]);
  const [dayEntries, setDayEntries] = useState<WorktimeEntry[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  // Convert selected date to millisecond timestamp (start of day UTC)
  const dateToMs = (dateStr: string): number => {
    const d = new Date(dateStr + 'T00:00:00Z');
    return d.getTime();
  };

  // Load remarks for autocomplete
  useEffect(() => {
    fetch(`${getBasePath()}getWorkerRemarks.php`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setRemarks(data);
      })
      .catch(() => {});
  }, []);

  // Load entries for selected date
  const loadDayEntries = useCallback(() => {
    const dayMs = dateToMs(selectedDate);
    fetch(`${getBasePath()}getWorktimeForDay.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ day: dayMs }),
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setDayEntries(data);
      })
      .catch(() => {});
  }, [selectedDate]);

  useEffect(() => {
    loadDayEntries();
  }, [loadDayEntries]);

  // Calculate net work time
  const parseTime = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return { h: h || 0, m: m || 0 };
  };

  const startMs = (() => { const t = parseTime(startTime); return timeToMs(t.h, t.m); })();
  const endMs = (() => { const t = parseTime(endTime); return timeToMs(t.h, t.m); })();
  const pauseMs = breakMinutes * 60000;
  const netMs = endMs - startMs - pauseMs;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsSaving(true);

    try {
      const response = await fetch(`${getBasePath()}saveWorktime.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          datum: dateToMs(selectedDate),
          beginn: startMs,
          ende: endMs,
          pause: pauseMs,
          bemerkung: bemerkung.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Speichern fehlgeschlagen');
        setIsSaving(false);
        return;
      }

      setSuccessMsg('Arbeitszeit gespeichert!');
      setBemerkung('');
      loadDayEntries();

      // Add new remark to list if it's new
      const trimmed = bemerkung.trim();
      if (trimmed && !remarks.includes(trimmed)) {
        setRemarks(prev => [...prev, trimmed].sort());
      }

      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Netzwerkfehler');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entryId: number) => {
    try {
      const response = await fetch(`${getBasePath()}deleteWorktime.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id: entryId }),
      });

      const data = await response.json();
      if (data.success) {
        loadDayEntries();
        setDeleteConfirm(null);
      } else {
        setError(data.error || 'Löschen fehlgeschlagen');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
  };

  // Check for overlapping entries
  const hasOverlap = (entries: WorktimeEntry[]): boolean => {
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        if (entries[i].Beginn < entries[j].Ende && entries[j].Beginn < entries[i].Ende) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    <div className="time-entry">
      <h2>Zeiten erfassen</h2>
      <p className="time-entry-subtitle">Hallo {user?.name || 'Mitarbeiter'}</p>

      {error && <div className="time-entry-error">{error}</div>}
      {successMsg && <div className="time-entry-success">{successMsg}</div>}

      <form onSubmit={handleSave} className="time-entry-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Datum</label>
            <input
              id="date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>

        <div className="form-row form-row-times">
          <div className="form-group">
            <label htmlFor="start">Beginn</label>
            <input
              id="start"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="end">Ende</label>
            <input
              id="end"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="break">Pause (Min.)</label>
            <input
              id="break"
              type="number"
              min="0"
              max="480"
              value={breakMinutes}
              onChange={(e) => setBreakMinutes(parseInt(e.target.value) || 0)}
            />
          </div>
        </div>

        {netMs > 0 && (
          <div className="time-entry-net">
            Netto-Arbeitszeit: <strong>{msToDuration(netMs)}</strong>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="remark-select">Vorherige Tätigkeiten</label>
          <select
            id="remark-select"
            value=""
            onChange={(e) => {
              if (e.target.value) setBemerkung(e.target.value);
            }}
          >
            <option value="">-- Auswählen oder unten neu eingeben --</option>
            {remarks.map((r, i) => (
              <option key={i} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="bemerkung">
            Tätigkeitsbeschreibung
            <span className="form-hint">
              {bemerkung && !remarks.includes(bemerkung.trim())
                ? ' (neue Beschreibung)'
                : ''}
            </span>
          </label>
          <textarea
            id="bemerkung"
            rows={3}
            value={bemerkung}
            onChange={(e) => setBemerkung(e.target.value)}
            placeholder="Was wurde gemacht? z.B. Stallarbeit, Fütterung, Paddock..."
            required
          />
        </div>

        <button type="submit" disabled={isSaving || netMs <= 0 || !bemerkung.trim()}>
          {isSaving ? 'Speichern...' : 'Speichern'}
        </button>
      </form>

      {/* Day entries list */}
      {dayEntries.length > 0 && (
        <div className="day-entries">
          <h3>Einträge für {new Date(selectedDate + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>

          {hasOverlap(dayEntries) && (
            <div className="time-entry-warning">
              Achtung: Es gibt überlappende Zeiteinträge an diesem Tag!
            </div>
          )}

          <div className="entries-list">
            {dayEntries.map(entry => (
              <div key={entry.ID} className="entry-card">
                <div className="entry-times">
                  <span className="entry-range">
                    {msToTime(entry.Beginn)} – {msToTime(entry.Ende)}
                  </span>
                  {entry.Pause > 0 && (
                    <span className="entry-break">Pause: {msToDuration(entry.Pause)}</span>
                  )}
                  <span className="entry-net">
                    Netto: {msToDuration(entry.Arbeitszeit)}
                  </span>
                </div>
                <div className="entry-remark">{entry.Bemerkung}</div>
                <div className="entry-actions">
                  {deleteConfirm === entry.ID ? (
                    <div className="delete-confirm">
                      <span>Wirklich löschen?</span>
                      <button className="btn-confirm-yes" onClick={() => handleDelete(entry.ID)}>Ja</button>
                      <button className="btn-confirm-no" onClick={() => setDeleteConfirm(null)}>Nein</button>
                    </div>
                  ) : (
                    <button className="btn-delete" onClick={() => setDeleteConfirm(entry.ID)}>Löschen</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeEntry;
